import * as React from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { Label } from "../label";

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  isError?: boolean;
  label?: string;
  labelClassName?: string;
  containerClassName?: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      isError = false,
      label,
      labelClassName,
      containerClassName,
      options,
      placeholder,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const selectId = id ?? (label ? generatedId : undefined);

    const borderColor = isError ? "border-destructive" : "border-input";

    const selectWrapper = (
      <div className={cn("relative", !label && containerClassName)}>
        <select
          id={selectId}
          className={cn(
            "h-12 w-full appearance-none rounded-lg border bg-muted px-3 pr-12 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
            borderColor,
            className
          )}
          ref={ref}
          {...props}
        >
          {placeholder && (
            <option value="" className="bg-popover">
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-foreground" />
      </div>
    );

    if (label) {
      return (
        <div className={cn("flex flex-col gap-3", containerClassName)}>
          <Label
            htmlFor={selectId}
            className={cn("justify-center self-stretch", labelClassName)}
          >
            {label}
          </Label>
          {selectWrapper}
        </div>
      );
    }

    return selectWrapper;
  }
);

Select.displayName = "Select";

export default Select;
